<template>
  <div class="background" :class="{ 'with-waves': !route.meta.public }">
    <div class="wave-top"></div>
    <div class="wave-bottom"></div>
    <template v-if="!route.meta.public">
      <div class="left-sidebar is-fullheight">
        <LeftSidebar />
      </div>
      <div class="main is-fullheight" :class="{ 'sidebar-open': appStore.rightSidebar }">
        <RouterView />
      </div>
      <div class="right-sidebar" :class="{ 'is-collapsed': !appStore.rightSidebar }">
        <RightSidebar />
      </div>
    </template>
    <template v-else>
      <RouterView />
    </template>
    <p style="position: relative">
      <o-loading v-model:active="appStore.isLoading" :full-page="true" :can-cancel="false">
        <o-icon icon="sync" size="large" rotation="45" />

        <span
          v-if="appStore.takesLonger.includes(route.name as string)"
          class="has-background-warning"
          style="margin-top: 80px; padding-left: 16px; padding-right: 16px"
        >
          This may take few minutes... {{ route.path.toString().split('/')[0] }}</span
        >
      </o-loading>
      </p>
   
  </div>
</template>

<script setup lang="ts">
import { onMounted, inject, computed, ref } from 'vue'
import { RouterLink, RouterView, useRoute } from 'vue-router'

import LeftSidebar from './components/LeftSidebar.vue'
import RightSidebar from './components/RightSidebar.vue'

import { useCompanyStore } from './stores/company'
import { useDepartmentStore } from './stores/department'
import { usePositionStore } from './stores/position'

import { useCostStore } from './stores/cost'
import { useEmployeeStore } from './stores/employee'
import { useAccountStore } from './stores/account'

import { usePayslipStore } from './stores/payslip'
import { useTransactStore } from './stores/transact'
import { useDailyTimeStore } from './stores/dailytime'
import { useTableStore } from './stores/table'
import { usePeriodStore } from './stores/period'
import { useScheduleStore } from './stores/schedule'
import { useLoanStore } from './stores/loan'
import { useSettingsStore } from './stores/settings'

import { useAppStore } from './stores/app'
import { useAuthStore } from './stores/auth'
import { useUtil } from '@/composables/util'

import dayjs from 'dayjs'
import { useProgrammatic } from '@oruga-ui/oruga-next'
import { FORBIDDEN_EVENT } from '@/api/deviceHeader'
import type { Period } from './stores/types'

import ssTable from '@/json/ss.json'
import birTable from '@/json/bir.json'
import phTable from '@/json/ph.json'
import pfTable from '@/json/pf.json'
// import utc from 'dayjs/plugin/utc' // import plugin
// import timezone from 'dayjs/plugin/timezone';

// dayjs.extend(utc)
// dayjs.extend(timezone)

const spinner = ref(true)

// a change refused for the user's role: say so once, not once per failed save
const { oruga } = useProgrammatic()
let lastForbidden = 0
window.addEventListener(FORBIDDEN_EVENT, (event) => {
  if (Date.now() - lastForbidden < 5000) return
  lastForbidden = Date.now()
  oruga.notification.open({
    message: (event as CustomEvent).detail || 'Your role cannot change this data.',
    variant: 'warning',
    position: 'top',
    duration: 6000,
    closable: true,
  })
})

const my = useUtil()

const route = useRoute()

const appStore = useAppStore()
const authStore = useAuthStore()
const accountStore = useAccountStore()
const companyStore = useCompanyStore()
const departmentStore = useDepartmentStore()
const positionStore = usePositionStore()

const employeeStore = useEmployeeStore()
const payslipStore = usePayslipStore()
const transactStore = useTransactStore()
const dailyTimeStore = useDailyTimeStore()
const periodStore = usePeriodStore()
const tableStore = useTableStore()
const costStore = useCostStore()
const scheduleStore = useScheduleStore()
const loanStore = useLoanStore()
const settingsStore = useSettingsStore()

const _loadLocalStorage = () => {
  // init from localStorage
  const thisPeriod = localStorage.getItem('thisPeriod')
  if (thisPeriod) {
    appStore.thisPeriod = JSON.parse(thisPeriod)
  }

  const thisPayCycle = localStorage.getItem('thisPayCycle')
  if (thisPayCycle) {
    appStore.thisPayCycle = JSON.parse(thisPayCycle)
  }

}

onMounted(async () => {
  const authed = await authStore.checkAuth()
  if (!authed) return  // router guard will redirect to /login

  _loadLocalStorage()
  await settingsStore.load()

  const today = dayjs()

  accountStore.getAccounts().then(() => {
    if (accountStore.accounts.length == 0) {
      accountStore.generateAccounts()
    }
  })
  companyStore.getCompanies()
  departmentStore.getDepartments()
  positionStore.getPositions()

  employeeStore.getEmployees()
  await periodStore.periodsThisYear(String(appStore.thisYear))
  costStore.getCostCenters()
  scheduleStore.getSchedules()

  // calcPay collects loan amortizations, so the loans and their ledger have to
  // be present before the first recalc or every payslip would collect nothing
  loanStore.getLoans()
  loanStore.getLoanTrans()

  // govt tables are bundled JSON and only change with a deploy: when the
  // bundle's tables differ from the last visit, payslips of Open periods
  // must recalculate. Mark them dirty only — they recalc when next viewed.
  const tablesHash = String(
    JSON.stringify([ssTable, birTable, phTable, pfTable])
      .split('')
      .reduce((h, c) => ((h << 5) - h + c.charCodeAt(0)) | 0, 0)
  )
  if (localStorage.getItem('govtTablesHash') != tablesHash) {
    await payslipStore.dirtyAffectedPayslips({}, false)
    localStorage.setItem('govtTablesHash', tablesHash)
  }
  

  // .then(()=>{
  //   if (costStore.costCenters.length==0) {
  //     costStore.generateCostCenters()
  //   }
  // })

  if (appStore.thisPeriod) {

    appStore.isLoading = true
    payslipStore.getPeriodPayslips(appStore.thisPeriod).then(() => {
      dailyTimeStore.getPeriodDailyTimes(appStore.thisPeriod)
      transactStore.getPeriodTransactions(appStore.thisPeriod)
      // payslipStore.recalcPeriodPayslips(appStore.thisPeriod)
    })
    appStore.isLoading = false
  }
})

</script>

<style scoped>
.background {
  background-color: #f0f4ff;
  width: 100vw;
  padding: 3em 0;
  min-height: 100vh;
  box-sizing: border-box;
}

/* the top wave curve is 114px tall: keep page titles clear of it */
.background.with-waves {
  padding-top: 7.5em;
}

/* ── Indigo→Teal wave: top ── */
.wave-top {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 114px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 190' preserveAspectRatio='none'%3E%3Cdefs%3E%3ClinearGradient id='tg' x1='0%25' y1='0%25' x2='100%25' y2='0%25'%3E%3Cstop offset='0%25' stop-color='%234f46e5'/%3E%3Cstop offset='100%25' stop-color='%230ea5e9'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath fill='url(%23tg)' d='M0,0 L1440,0 L1440,110 C960,190 480,50 0,150 Z'/%3E%3C/svg%3E");
  background-size: 100% 100%;
  z-index: 3;
  pointer-events: none;
}

/* ── Teal→Indigo wave: bottom ── */
.wave-bottom {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 114px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 190' preserveAspectRatio='none'%3E%3Cdefs%3E%3ClinearGradient id='bg' x1='0%25' y1='0%25' x2='100%25' y2='0%25'%3E%3Cstop offset='0%25' stop-color='%230ea5e9'/%3E%3Cstop offset='100%25' stop-color='%234f46e5'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath fill='url(%23bg)' d='M0,40 C480,150 960,10 1440,80 L1440,190 L0,190 Z'/%3E%3C/svg%3E");
  background-size: 100% 100%;
  z-index: 3;
  pointer-events: none;
}

.left-sidebar {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  z-index: 4;
  background-color: #1e1b4b;
  display: flex;
  flex-direction: column;
}
/* wave-color glow painted over the solid sidebar, pointer-events: none so clicks pass through */
.left-sidebar::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
    to bottom,
    rgba(79, 70, 229, 0.55) 0%,
    transparent 22%,
    transparent 75%,
    rgba(14, 165, 233, 0.45) 100%
  );
  pointer-events: none;
  z-index: 3;
}

.right-sidebar {
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: 220px;
  overflow: hidden;
  z-index: 2;
  background-color: #1e1b4b;
  transition: width 0.2s ease;
  /* waves (z-index 3) paint over this sidebar at top and bottom */
}
.right-sidebar.is-collapsed {
  width: 38px;
}
/* teal at top-right, indigo at bottom-right — mirrors the wave gradient */
.right-sidebar::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
    to bottom,
    rgba(14, 165, 233, 0.50) 0%,
    transparent 22%,
    transparent 75%,
    rgba(79, 70, 229, 0.45) 100%
  );
  pointer-events: none;
  z-index: 3;
}

.main {
  margin-left: 130px;
  margin-right: 38px;
  margin-top: 0;
  min-height: 100vh;
  position: relative;
  z-index: 1;
  transition: margin-right 0.2s ease;
}

.main.sidebar-open {
  margin-right: 220px;
}
</style>
